/**
 * HeroStyleSettingsDialog - Dialog for selecting collection hero image style
 *
 * Feature: 653-collection-hero-settings
 *
 * Allows users to choose between two hero image styles:
 * - Collage: 3-image grid layout (first, middle, last photos with "+X MORE")
 * - First Image: Center-cropped first photo at 3:2 aspect ratio
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/atoms';
import { Modal } from '../Modal';
import { useUpdateCollectionHeroStyleGraphQL, type HeroStyle } from '@/hooks/graphql/useCollectionMutationsGraphQL';
import logger from '@/utils/logger';

export interface HeroStyleSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  collectionId: string;
  currentStyle: HeroStyle;
  onStyleChanged?: (newStyle: HeroStyle) => void;
}

interface StyleOption {
  value: HeroStyle;
  label: string;
  description: string;
}

const STYLE_OPTIONS: StyleOption[] = [
  {
    value: 'collage',
    label: 'Collage (3-Image Grid)',
    description: 'A grid layout showing the first, middle, and last photos with a "+X MORE" overlay indicating additional photos.',
  },
  {
    value: 'first_image',
    label: 'First Image',
    description: 'Center-cropped first photo from the collection at 3:2 aspect ratio.',
  },
];

/**
 * HeroStyleSettingsDialog - Selection dialog for collection hero image style
 *
 * Usage:
 *   <HeroStyleSettingsDialog
 *     open={isOpen}
 *     onClose={handleClose}
 *     collectionId="co1g..."
 *     currentStyle="collage"
 *     onStyleChanged={(newStyle) => console.log('Changed to:', newStyle)}
 *   />
 */
export function HeroStyleSettingsDialog({
  open,
  onClose,
  collectionId,
  currentStyle,
  onStyleChanged,
}: HeroStyleSettingsDialogProps) {
  const [selectedStyle, setSelectedStyle] = useState<HeroStyle>(currentStyle);
  const [error, setError] = useState<string | null>(null);
  const { updateCollectionHeroStyle, loading } = useUpdateCollectionHeroStyleGraphQL();

  // Sync selected style with current style when dialog opens or current style changes
  useEffect(() => {
    if (open) {
      setSelectedStyle(currentStyle);
      setError(null); // Clear error when dialog opens
    }
  }, [open, currentStyle]);

  const handleSave = async () => {
    if (selectedStyle === currentStyle) {
      onClose();
      return;
    }

    setError(null);
    logger.info('HeroStyle', 'Saving hero style', { collectionId, selectedStyle });

    try {
      await updateCollectionHeroStyle({
        collectionId,
        heroStyle: selectedStyle,
      });

      onStyleChanged?.(selectedStyle);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update hero style';
      logger.error('HeroStyle', 'Failed to update hero style', { error: err });
      setError(message);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        loading={loading}
        disabled={loading || selectedStyle === currentStyle}
      >
        Save
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Hero Image Style"
      footer={footer}
      size="md"
      closeOnEscape={!loading}
      closeOnOverlayClick={!loading}
    >
      <div className="space-y-4">
        <p className="text-muted text-sm">
          Choose how the hero image for this collection is generated. The hero image appears on collection cards and at the top of collection pages.
        </p>

        <div className="space-y-3">
          {STYLE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`
                flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors
                ${selectedStyle === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted hover:bg-muted/10'
                }
              `}
            >
              <input
                type="radio"
                name="heroStyle"
                value={option.value}
                checked={selectedStyle === option.value}
                onChange={() => setSelectedStyle(option.value)}
                className="mt-1 h-4 w-4 text-primary focus:ring-primary border-border"
              />
              <div className="flex-1">
                <span className="font-medium text-foreground">{option.label}</span>
                <p className="text-sm text-muted mt-1">{option.description}</p>
              </div>
            </label>
          ))}
        </div>

        <p className="text-xs text-muted mt-4">
          Note: If the collection has fewer than 3 photos and "Collage" is selected, it will automatically fall back to using the first image.
        </p>

        {error && (
          <div className="mt-4 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
